// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { RCAvatar, RCStarRating } from '@reverbdotcom/cadence/components';
import React from 'react';
import { ProductReviewCardFields } from '@reverbdotcom/commons/src/gql/graphql';
import I18n from 'i18n-js';
import { formatInternationalizedDate } from '@reverbdotcom/commons/src/date_utils';
import { ProductReviewVoteButton } from './ProductReviewVoteButton';
import { SanitizedRender } from '@reverbdotcom/commons';
import { ProductReviewFlagButton } from './ProductReviewFlagButton';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { ProductReviewAdminBlockButton } from './ProductReviewAdminBlockButton';

interface Props {
  review: ProductReviewCardFields.Fragment;
  cspId: string;
  trackingDisplayStyle: 'product' | 'csp';
  refreshed?: boolean;
}

const TRUNCATION_LIMIT = 300;
const TRUNCATION_HEIGHT_LIMIT = 72;

export function ProductReviewCard({ review, cspId, trackingDisplayStyle, refreshed }: Props) {
  const user = useUser();

  return (
    <div data-product-review-card className="d-flex fx-dir-col gap-2">
      <div className="d-flex fx-align-center gap-2 weight-bold">
        <RCStarRating
          rating={review.rating / 5}
          hideTooltip
        />
        {review.verified && !refreshed &&
          I18n.t('discovery.productReviews.card.verifiedPurchase')
        }
      </div>
      <h4 className={`size-120 weight-bold ${refreshed ? 'mtb-1' : ''}`}>
        {review.title}
      </h4>
      <div className="d-flex fx-justify-between fx-wrap gap-2">
        <div className="d-flex fx-align-center gap-2">
          <div className="width-rem-3">
            <RCAvatar
              userName={review.reviewer.shortname}
              thumbnailURL={review.reviewer.links.avatar.href}
            />
          </div>
          <span className={refreshed ? 'item2-product-reviews__content-bold-text' : ''}>
            {review.reviewer.shortname}
          </span>
        </div>
        { review.verified && refreshed &&
          <div className="item2-product-reviews__verified_purchase-refreshed">
            {I18n.t('discovery.productReviews.card.verifiedPurchase')}
          </div>
        }
      </div>
      { refreshed ?
        <SanitizedRender
          html={review.body}
          htmlTag="div"
          className="item2-product-reviews__body-refreshed"
          truncationHeight={TRUNCATION_HEIGHT_LIMIT}
          withoutFade
        />
        :
        <SanitizedRender
          html={review.body}
          htmlTag="p"
          className="mb-0"
          truncationLimit={TRUNCATION_LIMIT}
        />
      }

      <div className={refreshed ? 'item2-product-reviews__content-text' : 'size-90'}>
        {formatInternationalizedDate(review.createdAt)}
      </div>
      <div className="d-flex width-100 fx-justify-between">
        {!review.isMyReview &&
          <ProductReviewVoteButton
            reviewId={review.id}
            cspId={cspId}
            trackingDisplayStyle={trackingDisplayStyle}
            refreshed={refreshed}
            disabled={review.voted}
          />
        }
        <div>
          {!review.isMyReview &&
            <ProductReviewFlagButton cspId={cspId} productReviewId={review.id} reported={review.reported} />
          }
          {user.isAdmin &&
            <ProductReviewAdminBlockButton productReviewId={review.id} />
          }
        </div>
      </div>
      {!!review.voteCount &&
        <div className={refreshed ? 'item2-product-reviews__content-bold-text' : 'size-90'}>
          {I18n.t('discovery.productReviews.card.voteCount', { count: review.voteCount })}
        </div>
      }
    </div>
  );
}

export const ProductReviewCardFragment = gql`
  fragment ProductReviewCardFields on ProductReview {
    _id
    id
    title
    body
    rating
    createdAt
    voteCount
    verified
    voted
    isMyReview
    reported
    reviewer {
      shortname
      links {
        avatar {
          href
        }
      }
    }
  }
`;
